import {
  ArchiveRoomResponse,
  BlockListResponse,
  BlockResponse,
  DeliveredResponse,
  GetMessagesResponse,
  ResponseError,
  GetRoomsResponse,
  SeenResponse,
  TypingRequest,
  TypingStatus,
  UnBlockResponse,
  CheckOnlineStatusResponse,
  SendMessageResponse,
  DeleteMessageResponse,
  MessageFromRoom,
  Room,
  GetListingResponse,
  UnreadRoomsResponse,
  UnreadPostRoomsResponse,
  SeenAllRequest,
  UserInfoResponse,
  UpdateOnlineStatusPayload,
  UserStatus,
  Message,
  SeenAllResponse,
  SendMessageRequest,
  LightListingView,
} from '../utils/types';

import {SocketActionTypes, SocketEvents} from '../utils/enums';
import {Socket} from 'socket.io-client';

export type SocketAction =
  | {type: SocketActionTypes.RESET_SESSION}
  | {type: SocketActionTypes.REMOVE_QUEUED_MESSAGE; response: {room_id: string; message_id: number}}
  | {type: SocketActionTypes.SET_SOCKET; payload: Socket}
  | {type: SocketActionTypes.CONNECT}
  | {type: SocketActionTypes.DISCONNECT; reason?: string}
  | {type: SocketActionTypes.CONNECT_ERROR; error: Error}
  | {type: SocketActionTypes.BLOCK_USER; response: BlockResponse}
  | {type: SocketActionTypes.UNBLOCK_USER; response: UnBlockResponse}
  | {type: SocketActionTypes.GET_BLOCK_LIST; response: BlockListResponse}
  | {type: SocketActionTypes.EMIT_DELIVERED; response: DeliveredResponse | ResponseError}
  | {type: SocketActionTypes.EMIT_SEEN; response: SeenResponse}
  | {type: SocketActionTypes.EMIT_TYPING; isTypingOwner: boolean}
  | {type: SocketActionTypes.EMIT_ARCHIVE_ROOM; response: ArchiveRoomResponse}
  | {type: SocketActionTypes.GET_ROOMS; response: GetRoomsResponse}
  | {
      type: SocketActionTypes.GET_MESSAGES;
      payload: {room_id: string; sync_timestamp?: Number};
      response: GetMessagesResponse;
    }
  | {
      type: SocketActionTypes.SET_TYPING_STATUS;
      payload: TypingRequest;
    }
  | {
      type: SocketActionTypes.CHECK_ONLINE_STATUS;
      response: CheckOnlineStatusResponse;
    }
  | {
      type: SocketActionTypes.UPDATE_ONLINE_STATUS;
      response: UserStatus;
    }
  | {type: SocketActionTypes.SEND_MESSAGE; response: SendMessageResponse}
  | {type: SocketActionTypes.DELETE_MESSAGE; response: DeleteMessageResponse}
  | {type: SocketActionTypes.SELECT_ROOM; roomId?: string; roomObject?: Room}
  | {
      type: SocketActionTypes.UPDATE_MESSAGES;
      payload: MessageFromRoom;
    }
  | {
      type: SocketActionTypes.SET_LISTING;
      response: GetListingResponse;
    }
  | {
      type: SocketActionTypes.HANDLE_DELIVERED;
      payload: {message_id: string; room_id: string};
    }
  | {
      type: SocketActionTypes.HANDLE_SEEN;
      payload: {message_id: string; room_id: string};
    }
  | {
      type: SocketActionTypes.HANDLE_ARCHIVE;
      payload: {rooms_ids: Array<string>};
    }
  | {
      type: SocketActionTypes.HANDLE_BLOCK;
      payload: {room_id: string; user_id: string};
    }
  | {type: SocketActionTypes.GET_SYSTEM_MESSAGES_ROOMS; response: GetRoomsResponse}
  | {
      type: SocketActionTypes.ROOMS_UNREAD_COUNT;
      response: UnreadRoomsResponse;
    }
  | {
      type: SocketActionTypes.POST_UNREAD_ROOMS;
      response: UnreadPostRoomsResponse;
    }
  | {
      type: SocketActionTypes.GET_ROOMS_WITH_OPTIONS;
      response: GetRoomsResponse;
    }
  | {
      type: SocketActionTypes.SEEN_ALL;
      response: SeenAllRequest;
    }
  | {
      type: SocketActionTypes.USER_INFO;
      response: UserInfoResponse;
    }
  | {
      type: SocketActionTypes.ADD_FAILED_MESSAGES;
      response: {messageData: SendMessageRequest; room_id: string};
    }
  | {
      type: SocketActionTypes.UPDATE_FAILED_MESSAGES;
      response: {messages: SendMessageRequest[]; room_id: string};
    }
  | {
      type: SocketActionTypes.ADD_PENDING_MESSAGES;
      response: {messageData: SendMessageRequest; room_id: string};
    }
  | {
      type: SocketActionTypes.UPDATE_PENDING_MESSAGES;
      response: {messages: SendMessageRequest[]; room_id: string};
    }
  | {
      type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS;
      response: Room | null;
    }
  | {
      type: SocketActionTypes.HANDLE_DELETE;
      payload: {message_id: number; room_id: string; user_id: number};
    }
  | {
      type: SocketActionTypes.SET_FILTER_STATUS;
      payload: {
        filter_by: 'all' | 'buyer' | 'seller' | 'listing_id' | 'verified';
        unread_rooms: boolean;
        system_messages: boolean;
      };
    }|
    {
      type: SocketActionTypes.SET_MEMBER;
      response: GetListingResponse;
    }
    |
    {
      type: SocketActionTypes.SET_VERIFICATION_LEVEL;
      response: number;
    };

export interface SocketState {
  socket: Socket | null;
  connectionStatus: string;
  blockResponse: BlockResponse | null;
  unblockResponse: UnBlockResponse | null;
  blockListResponse: BlockListResponse | null;
  deliveredResponse: DeliveredResponse | ResponseError | null;
  seenResponse: SeenResponse | null;
  archiveRoomResponse: ArchiveRoomResponse | null;
  roomListResponse: GetRoomsResponse;
  getMessagesResponse: Record<string, GetMessagesResponse>; // Add this line
  isTypingUser: boolean;
  typingStatus: TypingStatus;
  typingTimeouts: {[roomId: string]: NodeJS.Timeout};
  allUsersStatuses: UserStatus[];
  sendMessageResponse: SendMessageResponse | null;
  deleteMessageResponse: DeleteMessageResponse | null;
  selectedRoom: string | null;
  selectedRoomDetails: Room | null;
  listing: LightListingView | any;
  // userIdentity: Record<string, any> | null
  systemMessageRooms: GetRoomsResponse;
  unreadRoomsCount: UnreadRoomsResponse;
  unreadPostRoomsCounts: Array<{[key: string]: UnreadPostRoomsResponse}> | null;
  seenAllResponse: SeenAllRequest;
  userInfoResponse: UserInfoResponse | null;
  failedMessages: {[key: string]: SendMessageRequest[]} | null;
  pendingMessages: {[key: string]: SendMessageRequest[]} | null;
  filter: {
    filter_by: 'all' | 'buyer' | 'seller' | 'listing_id' | 'verified';
    unread_rooms: boolean;
    system_messages: boolean;
  };
  memberInfo:any;
  verification_level:number;
}

export const initialState: SocketState = {
  socket: null,
  connectionStatus: 'connecting',
  blockResponse: null,
  unblockResponse: null,
  blockListResponse: null,
  deliveredResponse: null,
  seenResponse: null,
  archiveRoomResponse: null,
  roomListResponse: {
    rooms: [],
    latest_room_timestamp: null,
    success: false,
    message: '',
  },
  getMessagesResponse: {},
  isTypingUser: false,
  typingStatus: [],
  typingTimeouts: {},
  allUsersStatuses: [],
  sendMessageResponse: null,
  deleteMessageResponse: null,
  selectedRoom: null,
  selectedRoomDetails: null,
  listing: null,
  // userIdentity: null, // Initialize with null
  systemMessageRooms: {
    rooms: [],
    latest_room_timestamp: null,
    success: false,
    message: '',
  },
  unreadRoomsCount: {
    data: {
      success: false,
      rooms_unread_count: 0,
    },
  },
  unreadPostRoomsCounts: null,
  seenAllResponse: {room_ids: []},
  userInfoResponse: null,
  failedMessages: {} as {[key: string]: {message: Message; room: Room}[]},
  pendingMessages: {} as {[key: string]: {message: Message; room: Room}[]},

  filter: {
    filter_by: 'all',
    unread_rooms: false,
    system_messages: false,
  },
  memberInfo:null,
  verification_level:0
};

export function roomHasUser(room: Room | null | undefined, userId: string | number): boolean {
  return !!room && (String(room.seller?.id) === String(userId) ||
    String(room.buyer?.id) === String(userId));
}

export function socketReducer(state: SocketState, action: SocketAction): SocketState {
  switch (action.type) {
    case SocketActionTypes.RESET_SESSION:
      return {...initialState, filter: state.filter};
    case SocketActionTypes.REMOVE_QUEUED_MESSAGE: {
      const {room_id, message_id} = action.response;
      return {
        ...state,
        pendingMessages: {...state.pendingMessages, [room_id]:
          state.pendingMessages?.[room_id]?.filter(item => item.message.id !== message_id) || []},
        failedMessages: {...state.failedMessages, [room_id]:
          state.failedMessages?.[room_id]?.filter(item => item.message.id !== message_id) || []},
      };
    }
    case SocketActionTypes.SET_SOCKET:
      return {...state, socket: action.payload};
    case SocketActionTypes.CONNECT:
      return {...state, connectionStatus: SocketEvents.CONNECT};

    case SocketActionTypes.DISCONNECT:
      return {...state, connectionStatus: action.reason || 'disconenect'};

    case SocketActionTypes.CONNECT_ERROR:
      return {...state, connectionStatus: SocketEvents.CONNECT_ERROR};
    case SocketActionTypes.ADD_PENDING_MESSAGES:
      return {
        ...state,
        failedMessages: {...state.failedMessages, [action.response.room_id]:
          state.failedMessages?.[action.response.room_id]?.filter(
            msg => msg.message.id !== action.response.messageData.message.id) || []},
        pendingMessages: {
          ...state.pendingMessages,
          [action.response.room_id]: [
            ...(state.pendingMessages?.[action.response.room_id]?.filter(
              (msg) => msg.message.id !== action.response.messageData.message.id
            ) || []),
            action.response.messageData,
          ],
        },
      };

    case SocketActionTypes.ADD_FAILED_MESSAGES:
      return {
        ...state,
        pendingMessages: {...state.pendingMessages, [action.response.room_id]:
          state.pendingMessages?.[action.response.room_id]?.filter(
            msg => msg.message.id !== action.response.messageData.message.id) || []},
        failedMessages: {
          ...state.failedMessages,
          [action.response.room_id]: [
            ...(state.failedMessages?.[action.response.room_id]?.filter(
              (msg) => msg.message.id !== action.response.messageData.message.id // Compare by message ID or any unique field
            ) || []),
            action.response.messageData,
          ],
        },
      };

    case SocketActionTypes.UPDATE_FAILED_MESSAGES:
      return {
        ...state,
        failedMessages: {
          ...state.failedMessages,
          [action.response.room_id]: action.response.messages, // Store full messages list
        },
      };
case SocketActionTypes.SET_VERIFICATION_LEVEL:
      return {
        ...state,
       verification_level:action.response
      };
    case SocketActionTypes.UPDATE_PENDING_MESSAGES:
      return {
        ...state,
        pendingMessages: {
          ...state.pendingMessages,
          [action.response.room_id]: action.response.messages,
        },
      };

    case SocketActionTypes.USER_INFO:
      return {...state, userInfoResponse: action.response};
    case SocketActionTypes.SET_SELECTED_ROOM_DETAILS: {
      const selectedRoom = action.response;
      const roomId = selectedRoom?.room_id;

      return {
        ...state,
        selectedRoomDetails: selectedRoom,
      };
    }

    case SocketActionTypes.BLOCK_USER: {
      const {room_id, user_id} = action.response.data;
      const shouldResetSelection =
        state.selectedRoom === room_id ||
        state.selectedRoomDetails?.room_id === room_id ||
        roomHasUser(state.selectedRoomDetails, user_id) ||
        [...state.roomListResponse.rooms, ...state.systemMessageRooms.rooms].some(
          room => room.room_id === state.selectedRoom && roomHasUser(room, user_id));

      return {
        ...state,
        blockResponse: action.response,
        selectedRoom: shouldResetSelection ? null : state.selectedRoom,
        selectedRoomDetails: shouldResetSelection ? null : state.selectedRoomDetails,
        roomListResponse: {
          ...state.roomListResponse,
          rooms: state.roomListResponse.rooms.filter(room => !roomHasUser(room, user_id)),
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: state?.systemMessageRooms?.rooms?.filter(
            (room) => !roomHasUser(room, user_id)
          ),
        },
      };
    }

    case SocketActionTypes.UNBLOCK_USER:
      return {...state, unblockResponse: action.response};
    case SocketActionTypes.GET_ROOMS_WITH_OPTIONS:
      if (!action.response.success) return state;
      const blockedUsers = new Set(
        state?.blockListResponse?.data?.items?.map((user) => user?.user_id)
      );

      // Filter incoming rooms to only include new ones
      const finalRoomList = action.response?.rooms?.filter((room) => {
        // Check if the room is new and does not contain a blocked user in its ID
        const containsBlockedUser = Array.from(blockedUsers).some((userId) =>
          roomHasUser(room, userId)
        );

        return !containsBlockedUser;
      });

      action.response = {
        ...action.response,
        rooms: finalRoomList,
      };
      return {...state, systemMessageRooms: action.response};

    case SocketActionTypes.GET_BLOCK_LIST: {
      const {items} = action.response.data;

      // Extract user_ids from the blocked list
      const blockedUserIds = items.map((blockedItem) => blockedItem.user_id);

      return {
        ...state,
        blockListResponse: action.response,
      };
    }

    case SocketActionTypes.EMIT_DELIVERED:
      return {
        ...state,
        deliveredResponse: action.response,
      };

    case SocketActionTypes.EMIT_SEEN: {
      const roomId = action.response.room_id;
      // Fetch the unread count before updating the room list
      const unreadCountBeforeUpdate =
        state?.roomListResponse?.rooms?.find((room) => room.room_id === roomId)?.unread_count ?? 0; // Default to 0 if room is not found

      // Update unread_count to 0 in roomListResponse
      const updatedRoomListResponse = {
        ...state.roomListResponse,
        rooms: state.roomListResponse?.rooms.map((room) =>
          room.room_id === roomId ? {...room, unread_count: 0} : room
        ),
      };

      // Update unread_count to 0 in systemMessageRooms
      const updatedSystemMessageRooms = {
        ...state.systemMessageRooms,
        rooms: state.systemMessageRooms?.rooms?.map((room) =>
          room.room_id === roomId ? {...room, unread_count: 0} : room
        ),
      };

      // Calculate new unread rooms count correctly
      const newCount =
        (state?.unreadRoomsCount?.data?.rooms_unread_count ?? 0) - unreadCountBeforeUpdate;

      return {
        ...state,
        seenResponse: action.response,
        roomListResponse: updatedRoomListResponse,
        systemMessageRooms: updatedSystemMessageRooms,
        unreadRoomsCount: {
          ...state.unreadRoomsCount,
          data: {
            ...state.unreadRoomsCount.data,
            rooms_unread_count: newCount, 
          },
        },
      };
    }

    case SocketActionTypes.SET_FILTER_STATUS:
      return {
        ...state,
        filter: action.payload, // Store the filter object
      };
    case SocketActionTypes.ROOMS_UNREAD_COUNT:
      return {...state, unreadRoomsCount: action.response};
    case SocketActionTypes.POST_UNREAD_ROOMS:
      const unreadPostRoomsCounts = state.unreadPostRoomsCounts || [];

      // Check if an entry for the specific post_id already exists
      const updatedRooms = unreadPostRoomsCounts.filter(
        (room) => Object.keys(room)[0] !== String(action.response.post_id)
      );

      // Create a new entry for the post_id and add it to the array
      const newEntry = {
        [String(action.response.post_id)]: action.response,
      };

      updatedRooms.push(newEntry);

      return {
        ...state,
        unreadPostRoomsCounts: updatedRooms,
      };

    case SocketActionTypes.EMIT_TYPING:
      return {...state, isTypingUser: action.isTypingOwner};

    case SocketActionTypes.EMIT_ARCHIVE_ROOM: {
      const rooms_ids = action.response.rooms_ids || [];
      const shouldResetSelection = state.selectedRoom && rooms_ids.includes(state.selectedRoom);

      return {
        ...state,
        selectedRoom: shouldResetSelection ? null : state.selectedRoom, // Reset only if current room is archived
        selectedRoomDetails: shouldResetSelection ? null : state.selectedRoomDetails, // Reset only if current room is archived
        roomListResponse: {
          ...state.roomListResponse,
          rooms: state.roomListResponse.rooms?.filter(
            (room) => !rooms_ids?.includes(room?.room_id) // Filter out archived rooms
          ),
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: state.systemMessageRooms.rooms?.filter(
            (room) => !rooms_ids?.includes(room?.room_id) // Filter out archived rooms
          ),
        },
        archiveRoomResponse: action.response, // Update archive response
        getMessagesResponse: {
          ...state.getMessagesResponse,
          ...Object.fromEntries(
            rooms_ids
              .filter((room_id) => state.getMessagesResponse[room_id]) // Only update existing keys
              .map((room_id) => [
                room_id,
                {
                  ...state.getMessagesResponse[room_id],
                  messages: [],
                },
              ])
          ),
        },
      };
    }

    case SocketActionTypes.GET_ROOMS:
      if (action.response.message === 'Socket is not connected') {
        return {...state};
      }

      if (!action.response.success || 'errorCode' in action.response) {
        return {
          ...state,
          roomListResponse: {
            ...state.roomListResponse,
            success: false,
            message: action.response.message,
            rooms: state.roomListResponse.rooms, // Keep existing rooms
            latest_room_timestamp: state.roomListResponse.latest_room_timestamp,
          },
        };
      }

      // Blocked users setup
      const blockedUsersSet = new Set(
        state?.blockListResponse?.data?.items?.map((user) => user?.user_id)
      );

      // Create a new map with only new rooms
      const newRoomss = action.response.rooms || [];
      const roomMap = new Map(newRoomss.map((room) => [room.room_id, room]));

      // Add old rooms that are not in the newRooms
      (state.roomListResponse?.rooms || []).forEach((room) => {
        if (!roomMap.has(room.room_id)) {
          roomMap.set(room.room_id, room);
        }
      });

      // Filter out rooms from blocked users
      const filteredRooms = Array.from(roomMap.values()).filter(
        (room) => !Array.from(blockedUsersSet).some((userId) => roomHasUser(room, userId))
      );

      // Sort rooms by timestamp in descending order
      const mergedRooms = filteredRooms.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
      const getMessagesResponse = {...state.getMessagesResponse};
      action.response.rooms.forEach((room) => {
        const existingMessages = state.getMessagesResponse[room?.room_id]?.messages || [];
        const newMessages = room.messages || [];

        // A refreshed server copy wins over the cached copy of the same message.
        const combinedMessages = [...existingMessages, ...newMessages];

        // Remove duplicates using a Map (unique by message ID)
        const uniqueMessages = Array.from(
          new Map(combinedMessages.map((msg) => [msg.id, msg])).values()
        );

        // Sort messages by timestamp to maintain the correct order
        getMessagesResponse[room?.room_id] = {
          ...state.getMessagesResponse[room?.room_id],
          messages: uniqueMessages.sort((a, b) => Number(b.sent_at) - Number(a.sent_at)),
          // Refreshing recent messages must not move the older-history cursor forward.
          sync_timestamp: state.getMessagesResponse[room.room_id]?.sync_timestamp ?? room.sync_timestamp,
          has_more: state.getMessagesResponse[room.room_id]?.has_more ?? room.has_more,
          success: true,
          message: '',
        };
      });

      return {
        ...state,
        roomListResponse: {
          ...state.roomListResponse,
          success: action.response.success,
          message: action.response.message,
          rooms: mergedRooms,
          latest_room_timestamp: action.response.latest_room_timestamp,
        },
        getMessagesResponse,
      };

    case SocketActionTypes.GET_SYSTEM_MESSAGES_ROOMS:
      const existingRoomIds = new Set(
        state?.systemMessageRooms?.rooms?.map((room) => room?.room_id)
      );

      const newRooms = action.response.rooms?.filter((room) => !existingRoomIds.has(room?.room_id));
      return {
        ...state,
        systemMessageRooms: {
          ...state.systemMessageRooms,
          success: action.response.success,
          message: action.response.message,
          rooms: [...state.systemMessageRooms.rooms, ...newRooms].sort(
            (a, b) => Number(b.timestamp) - Number(a.timestamp)
          ),
          latest_room_timestamp: action.response.latest_room_timestamp,
        },
      };

    case SocketActionTypes.GET_MESSAGES: {
      // A failed page is retryable; retain both the messages and its cursor.
      if (!action.response.success) return state;
      const {room_id} = action.payload; // Extract room_id from payload
      const cached = state.getMessagesResponse[room_id];
      const keepHistoryCursor = action.payload.sync_timestamp == null && cached?.sync_timestamp != null;
      const currentMessages = state.getMessagesResponse[room_id]?.messages || [];
      const updatedMessages = [...currentMessages, ...action.response?.messages];
      const uniqueMessages = Array.from(
        new Map(updatedMessages.map((msg) => [msg.id, msg])).values()
      );

      uniqueMessages.sort((a, b) => Number(b.sent_at) - Number(a.sent_at));

      return {
        ...state,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          [room_id]: {
            ...action.response,
            messages: uniqueMessages,
            sync_timestamp: keepHistoryCursor ? cached.sync_timestamp : action.response.sync_timestamp,
            has_more: keepHistoryCursor ? cached.has_more : action.response.has_more,
          },
        },
      };
    }

    case SocketActionTypes.SET_TYPING_STATUS: {
      const {room_id, is_typing} = action.payload;
      const existingRoom = state.typingStatus.find((room) => room?.room_id == room_id);

      let updatedTypingStatus: TypingStatus;

      if (existingRoom) {
        updatedTypingStatus = state.typingStatus.map((room) =>
          room?.room_id == room_id ? {...room, is_typing} : room
        );
      } else {
        updatedTypingStatus = [...state.typingStatus, {room_id, is_typing}];
      }

      return {
        ...state,
        typingStatus: updatedTypingStatus,
      };
    }
    case SocketActionTypes.CHECK_ONLINE_STATUS: {
      const { response } = action;
    
      if (Array.isArray(response) && response.length > 0) {
        const updatedStatuses = response
          .map(({ room_id, user_id, status, ...rest }) => ({
            user_id: +user_id, // Convert user_id to number
            status: status ? 1 : 0, // Convert boolean status to number (1 for true, 0 for false)
            socket: null, // or some default value
            ...rest,
          }));
    
        // Merge old statuses with the new ones, updating where user_id matches
        const allUsersStatuses = [
          ...state.allUsersStatuses.filter(
            (status) => !updatedStatuses.some((update) => update.user_id === status.user_id)
          ),
          ...updatedStatuses,
        ];
    
        return {
          ...state,
          allUsersStatuses,
        };
      } else {
        return state;
      }
    }
    

    case SocketActionTypes.UPDATE_ONLINE_STATUS: {
      const {user_id, status} = action.response;

      const userExists = state?.allUsersStatuses?.some((user) => +user?.user_id == user_id);

      const updatedStatuses = userExists
        ? state?.allUsersStatuses.map((user) =>
            +user?.user_id == user_id ? {...user, status, socket: user.socket} : user
          )
        : [
            ...state?.allUsersStatuses,
            {user_id: user_id, status, socket: null}, // Make sure to add socket here as well
          ];

      return {
        ...state,
        allUsersStatuses: updatedStatuses,
      };
    }

    case SocketActionTypes.SEND_MESSAGE: {
      const newMessage = action.response.data.message;
      const targetRoom = action.response.data.room;

      // Map through the rooms and update only the target room
      const updatedRooms = state.roomListResponse.rooms.map((room) => {
        if (room?.room_id === targetRoom?.room_id) {
          return {
            ...room,
            ...targetRoom,
            messages: [newMessage, ...(room?.messages || [])],
            last_message: newMessage,
          };
        }
        return room;
      });

      const updatedRoom = updatedRooms.find((room) => room?.room_id === targetRoom?.room_id);
      const otherRooms = updatedRooms.filter((room) => room?.room_id !== targetRoom?.room_id);

      // Create the final reordered list with the updated room first
      const reorderedRooms = [updatedRoom, ...otherRooms];

      return {
        ...state,
        sendMessageResponse:action.response
      };
    }

    case SocketActionTypes.DELETE_MESSAGE: {
      if (action.response.success === false) return state;
      const {room_id, message_id} = action.response.data;

      const cached = state.getMessagesResponse[room_id];
      if (!cached) {
        return state; // If no messages exist for the room, return current state
      }

      // Get the current messages in the room
      const currentMessages = cached.messages;

      // Filter out the deleted message
      const updatedMessages = currentMessages.filter((msg) => msg.id !== message_id);

      // Find the new last message after deletion
      const newLastMessage =
        updatedMessages[0] ?? null;

      // Update the roomListResponse to reflect the change
      const updatedRooms = state.roomListResponse.rooms.map((room) => {
        if (room?.room_id === room_id) {
          return {
            ...room,
            messages: updatedMessages,
            last_message: newLastMessage, // Update the last_message property
          };
        }
        return room;
      });

      // Update the systemMessageRooms to reflect the change
      const updatedSystemMessageRooms = state.systemMessageRooms?.rooms?.map((room) => {
        if (room?.room_id === room_id) {
          return {
            ...room,
            messages: updatedMessages,
            last_message: newLastMessage, // Update the last_message property
          };
        }
        return room;
      });

      return {
        ...state,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          [room_id]: {
            ...cached,
            messages: updatedMessages,
          },
        },
        roomListResponse: {
          ...state.roomListResponse,
          rooms: updatedRooms,
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: updatedSystemMessageRooms,
        },
      };
    }

    case SocketActionTypes.UPDATE_MESSAGES: {
      const {room, message, currentUser} = action.payload;
      const roomId = room?.room_id;

      // Assign a temporary ID if message has no ID
      if (!message.id) {
        message.id = Math.floor(Date.now() + Math.random() * 1000);
      }
      const newMessageId = message.id;

      // Update getMessagesResponse
      const currentMessages = state.getMessagesResponse[roomId]?.messages || [];
      if (currentMessages.some((msg) => msg.id === newMessageId)) {
        return state;
      }

      const updatedGetMessagesResponse = {
        ...state.getMessagesResponse,
        [roomId]: {
          success: true,
          message: '',
          has_more: true,
          ...state.getMessagesResponse[roomId],
          messages: [message, ...currentMessages],
          // Live messages do not advance the older-history cursor.
          sync_timestamp: state.getMessagesResponse[roomId]?.sync_timestamp,
        },
      };

      // Update roomListResponse
      const updatedRooms = state.roomListResponse.rooms.map((roomItem) => {
        if (roomItem.room_id === roomId) {
          const roomMessages = roomItem.messages || [];
          if (roomMessages.some((msg) => msg.id === newMessageId)) {
            return roomItem;
          }
          return {
            ...roomItem,
            messages: [message, ...roomMessages],
            last_message: message,
            last_snippet: message.body.text,
            unread_count:
              message.sender_id == currentUser
                ? roomItem.unread_count || 0
                : (roomItem.unread_count || 0) + 1,
            timestamp: Date.now(),
          };
        }
        return roomItem;
      });

      // Move updated room to the top
      updatedRooms.sort((a, b) => (a.room_id === roomId ? -1 : b.room_id === roomId ? 1 : 0));

      // Update systemMessageRooms
      let updatedSystemMessageRooms = state?.systemMessageRooms?.rooms?.map((roomItem) => {
        if (roomItem.room_id === roomId) {
          const roomMessages = roomItem.messages || [];
          if (roomMessages.some((msg) => msg.id === newMessageId)) {
            return roomItem;
          }
          return {
            ...roomItem,
            messages: [message, ...roomMessages],
            last_message: message,
            last_snippet: message.body.text,
            unread_count:
              message.sender_id === currentUser
                ? roomItem.unread_count || 0
                : (roomItem.unread_count || 0) + 1,
            timestamp: Date.now(),
          };
        }
        return roomItem;
      });

      // Append new room if not found in systemMessageRooms
      if (
        !updatedSystemMessageRooms?.some((roomItem) => roomItem?.room_id === roomId) &&
        message?.sender_id !== currentUser
      ) {
        updatedSystemMessageRooms?.push({
          ...room,
          messages: [message],
          last_message: message,
          last_snippet: message.body.text,
          unread_count: message.sender_id === currentUser ? 0 : 1,
          timestamp: Date.now(),
          sync_timestamp: newMessageId,
        });
      }

      // Move updated room to the top
      updatedSystemMessageRooms?.sort((a, b) =>
        a.room_id === roomId ? -1 : b.room_id === roomId ? 1 : 0
      );

      return {
        ...state,
        getMessagesResponse: updatedGetMessagesResponse,
        roomListResponse: {
          ...state.roomListResponse,
          rooms: updatedRooms,
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: updatedSystemMessageRooms,
        },
        unreadRoomsCount: {
          ...state.unreadRoomsCount,
          data: {
            ...state.unreadRoomsCount.data,
            rooms_unread_count:
              message.recipient_id == currentUser
                ? state.unreadRoomsCount.data.rooms_unread_count + 1
                : state.unreadRoomsCount.data.rooms_unread_count,
          },
        },
        sendMessageResponse:message.sender_id === currentUser?action.payload:state.sendMessageResponse
      };
    }

    case SocketActionTypes.SELECT_ROOM: {
      let updatedRoomListResponseRooms;
      let updatedSystemMessageRooms;

      if (action.roomObject) {
        // When the action includes a roomObject

        // Find the room details using action.roomObject?.room_id
        const selectedRoomDetails = state.roomListResponse.rooms?.find(
          (room) => room?.room_id === action.roomObject?.room_id
        );

        // Ensure synchronization: update selectedRoom and selectedRoomDetails
        return {
          ...state,
          selectedRoom: action.roomObject?.room_id, // Set selectedRoom
          selectedRoomDetails: selectedRoomDetails ?? action.roomObject, // Set selectedRoomDetails
        };
      }

      // Handle case where roomObject is not available, just use selectedRoom

      // Find the selectedRoomDetails based on state.selectedRoom
      const selectedRoomDetails = state.roomListResponse.rooms.find(
        (room) => room?.room_id === action.roomId
      );

      // Ensure synchronization: update selectedRoom and selectedRoomDetails
      return {
        ...state,
        selectedRoom: action.roomId ?? null, // Set selectedRoom from action.roomId
        selectedRoomDetails: selectedRoomDetails ?? null, // Set selectedRoomDetails based on selectedRoom
      };
    }

    case SocketActionTypes.SET_LISTING: {      
      return {
        ...state,
        listing: action.response,
      };
    }
    case SocketActionTypes.SET_MEMBER: {
      return {
        ...state,
        memberInfo: action.response,
      };
    }
    case SocketActionTypes.HANDLE_ARCHIVE: {
      const rooms_ids = action.payload.rooms_ids || [];

      const shouldResetSelection = state.selectedRoom && rooms_ids.includes(state.selectedRoom);

      return {
        ...state,
        selectedRoom: shouldResetSelection ? null : state.selectedRoom, // Reset only if current room is archived
        selectedRoomDetails: shouldResetSelection ? null : state.selectedRoomDetails, // Reset only if current room is archived
        roomListResponse: {
          ...state.roomListResponse,
          rooms: state.roomListResponse?.rooms?.filter(
            (room) => !rooms_ids.includes(room?.room_id) // Filter out archived rooms
          ),
        },
        systemMessageRooms: {
          ...state?.systemMessageRooms,
          rooms: state?.systemMessageRooms?.rooms?.filter(
            (room) => !rooms_ids.includes(room?.room_id) // Filter out archived rooms
          ),
        },
        archiveRoomResponse: action.payload, // Update archive response,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          ...Object.fromEntries(
            rooms_ids
              .filter((room_id) => state.getMessagesResponse[room_id]) // Only update existing keys
              .map((room_id) => [
                room_id,
                {
                  ...state.getMessagesResponse[room_id],
                  messages: [],
                },
              ])
          ),
        },
      };
    }
    case SocketActionTypes.HANDLE_BLOCK: {
      const {room_id, user_id} = action.payload;
      const shouldResetSelection =
        state.selectedRoom === room_id ||
        state.selectedRoomDetails?.room_id === room_id ||
        roomHasUser(state.selectedRoomDetails, user_id) ||
        [...state.roomListResponse.rooms, ...state.systemMessageRooms.rooms].some(
          room => room.room_id === state.selectedRoom && roomHasUser(room, user_id));

      return {
        ...state,
        blockResponse: {data: {...action.payload}},
        selectedRoom: shouldResetSelection ? null : state.selectedRoom,
        selectedRoomDetails: shouldResetSelection ? null : state.selectedRoomDetails,
        roomListResponse: {
          ...state.roomListResponse,
          rooms: state.roomListResponse.rooms.filter(room => !roomHasUser(room, user_id)),
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: state?.systemMessageRooms?.rooms?.filter(
            (room) => !roomHasUser(room, user_id)
          ),
        },
      };
    }

    case SocketActionTypes.HANDLE_DELIVERED: {
      const {room_id, message_id} = action.payload;

      // Update delivered_at for all messages up to and including the given message_id
      const updatedRoomMessages = state.getMessagesResponse[room_id]?.messages.map((message) => {
        if (message.id && message.id <= Number(message_id)) {
          return {...message, delivered_at: message.delivered_at || Date.now()}; // Only update if not already delivered
        }
        return message;
      });

      // Return updated state
      return {
        ...state,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          [room_id]: {
            ...state.getMessagesResponse[room_id],
            messages: updatedRoomMessages || [],
          },
        },
      };
    }

    case SocketActionTypes.HANDLE_SEEN: {
      const {room_id, message_id} = action.payload;

      const currentRoomMessages = state.getMessagesResponse[room_id] || {
        messages: [],
        sync_timestamp: 0,
        has_more: false, // Ensure has_more is explicitly set
        message: '',
        success: true,
      };
      const updatedRoomListResponse = state.roomListResponse
        ? {
            ...state.roomListResponse,
            rooms: state.roomListResponse.rooms?.map((room) =>
              room.room_id === room_id ? {...room, unread_count: 0} : room
            ),
          }
        : state.roomListResponse;

      const updatedRoomMessages = currentRoomMessages.messages.map((message) => {
        if (message.id && message.id <= Number(message_id)) {
          return {...message, seen_at: message.seen_at || Date.now()}; // Only update if not already seen
        }
        return message;
      });
      // Fetch the unread count before updating the room list
      let unreadCountBeforeUpdate =
        state?.roomListResponse?.rooms?.find((room) => room.room_id === room_id)?.unread_count; // Default to 0 if room is not found
      if(!unreadCountBeforeUpdate){
        unreadCountBeforeUpdate = state?.systemMessageRooms?.rooms?.find((room) => room.room_id === room_id)?.unread_count??0; // Default to 0 if room is not found

      }
      // Update unread_count to 0 in roomListResponse


      // Update unread_count to 0 in systemMessageRooms
      const updatedSystemMessageRooms = {
        ...state.systemMessageRooms,
        rooms: state.systemMessageRooms?.rooms?.map((room) =>
          room.room_id === room_id ? {...room, unread_count: 0} : room
        ),
      };

      // Calculate new unread rooms count correctly
      const newCount =
        (state?.unreadRoomsCount?.data?.rooms_unread_count ?? 0) - unreadCountBeforeUpdate;

      return {
        ...state,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          [room_id]: {
            ...currentRoomMessages,
            messages: updatedRoomMessages,
          } as GetMessagesResponse, // Explicitly assert the type
        },
        roomListResponse: updatedRoomListResponse,
        systemMessageRooms: updatedSystemMessageRooms,
        unreadRoomsCount: {
          ...state.unreadRoomsCount,
          data: {
            ...state.unreadRoomsCount.data,
            rooms_unread_count: newCount, 
          },
        },
      };
    }

    case SocketActionTypes.HANDLE_DELETE: {
      const {room_id, message_id, user_id} = action.payload;

      const cached = state.getMessagesResponse[room_id];
      if (!cached) {
        return state; // If no messages exist for the room, return current state
      }

      // Get the current messages in the room
      const currentMessages = cached.messages;

      // Filter out the deleted message
      const updatedMessages = currentMessages.filter((msg) => msg.id !== message_id);

      // Find the new last message after deletion
      const newLastMessage =
        updatedMessages[0] ?? null;

      // Update the roomListResponse to reflect the change
      const updatedRooms = state.roomListResponse.rooms.map((room) => {
        if (room?.room_id === room_id) {
          return {
            ...room,
            messages: updatedMessages,
            last_message: newLastMessage, // Update the last_message property
          };
        }
        return room;
      });
      const updatedSystemRooms = state.systemMessageRooms.rooms.map((room) => {
          if (room?.room_id === room_id) {
            return {
              ...room,
              messages: updatedMessages,
              last_message: newLastMessage, // Update the last_message property
            };
          }
          return room;
        });

      return {
        ...state,
        getMessagesResponse: {
          ...state.getMessagesResponse,
          [room_id]: {
            ...cached,
            messages: updatedMessages,
          },
        },
        roomListResponse: {
          ...state.roomListResponse,
          rooms: updatedRooms,
        },
        systemMessageRooms: {
          ...state.systemMessageRooms,
          rooms: updatedSystemRooms,
        },
      };
    }

    case SocketActionTypes.SEEN_ALL:
      const currentTimestamp = Date.now();

      return {
        ...state,
        seenAllResponse: action.response,
        getMessagesResponse: {
          ...state.getMessagesResponse, // Keep other rooms intact
          ...action.response?.room_ids.reduce((updatedRooms, roomId: string) => {
            const room = state.getMessagesResponse[roomId];

            if (room && room.messages.length > 0) {
              // Loop through all the messages and update seen_at if it's null or undefined
              const updatedMessages = room.messages.map((message) => {
                if (message.seen_at === null || message.seen_at === undefined) {
                  return {
                    ...message, // Spread the message to avoid mutation
                    seen_at: currentTimestamp,
                  };
                }
                return message; // Return the unmodified message if seen_at is already set
              });

              // Update the room with the modified messages
              updatedRooms[roomId] = {
                ...room, // Spread the room to preserve other properties
                messages: updatedMessages, // Replace the messages array
              };
            } else {
              // If the room has no messages, keep the room unchanged
              updatedRooms[roomId] = room;
            }

            return updatedRooms;
          }, {}),
        },
        unreadRoomsCount:{
          ...state.unreadRoomsCount,
          data:{
            success: true,
            rooms_unread_count: 0
          }
        }
      };

    default:
      return state;
  }
}
